import type { Property } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox, Field, Input, Select, Textarea } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, isConflict, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { STATUS_KEYS, TYPE_KEYS } from "@/lib/labels";
import { normalizePhone } from "@elektr/shared";

type Status = "draft" | "ready" | "archived";
type PType = "apartment" | "house" | "office" | "other";

interface FormState {
  type: PType;
  region: string;
  district: string;
  address: string;
  house_number: string;
  floor: string;
  area_m2: string;
  installed_at: string;
  warranty_until: string;
  notes: string;
  status: Status;
  show_address_publicly: boolean;
  owner_phone: string;
  electricians: string[];
}

function initial(p?: Property): FormState {
  return {
    type: (p?.type ?? "apartment") as PType,
    region: p?.region ?? "",
    district: p?.district ?? "",
    address: p?.address ?? "",
    house_number: p?.house_number ?? "",
    floor: p?.floor != null ? String(p.floor) : "",
    area_m2: p?.area_m2 ?? "",
    installed_at: p?.installed_at ?? "",
    warranty_until: p?.warranty_until ?? "",
    notes: p?.notes ?? "",
    status: (p?.status ?? "draft") as Status,
    show_address_publicly: p?.show_address_publicly ?? false,
    owner_phone: p?.owner_phone ?? "",
    electricians: p?.electricians ?? [],
  };
}

export function PropertyForm({
  property,
  onSaved,
}: {
  property?: Property;
  onSaved: (p: Property) => void;
}) {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const admin = isAdmin(user);

  const [f, setF] = useState<FormState>(() => initial(property));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState(false);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));

  const electricians = useQuery({
    queryKey: ["users", "electrician", "all"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/users/", {
          params: { query: { role: "electrician", page_size: 100 } },
        }),
      ),
    enabled: admin,
  });

  const save = useMutation({
    mutationFn: async () => {
      const ownerPhone = f.owner_phone.trim() ? normalizePhone(f.owner_phone) : "";
      if (ownerPhone === null) throw new Error("owner_phone");
      const body = {
        type: f.type,
        region: f.region,
        district: f.district,
        address: f.address,
        house_number: f.house_number,
        floor: f.floor === "" ? null : Number(f.floor),
        area_m2: f.area_m2 === "" ? null : f.area_m2,
        installed_at: f.installed_at || null,
        warranty_until: f.warranty_until || null,
        notes: f.notes,
        status: f.status,
        show_address_publicly: f.show_address_publicly,
        owner_phone: ownerPhone,
        ...(admin ? { electricians: f.electricians } : {}),
      };
      if (property) {
        return unwrap(
          await api.PATCH("/api/properties/{id}/", {
            params: { path: { id: property.id } },
            body: { ...body, version: property.version },
          }),
        );
      }
      return unwrap(
        await api.POST("/api/properties/", {
          body,
        }),
      );
    },
    onSuccess: (saved) => {
      setConflict(false);
      setErrors({});
      toast.success(t("common.saved"));
      void qc.invalidateQueries({ queryKey: ["properties"] });
      void qc.invalidateQueries({ queryKey: ["count"] });
      qc.setQueryData(["property", saved.id], saved);
      onSaved(saved);
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "owner_phone")
        return setErrors({ owner_phone: t("auth.phone_invalid") });
      if (isConflict(e)) return setConflict(true);
      const body = (e as { body?: Record<string, unknown> }).body;
      if (body && typeof body === "object") {
        const fieldErrors: Record<string, string> = {};
        for (const [k, v] of Object.entries(body))
          fieldErrors[k] = Array.isArray(v) ? v.join(", ") : String(v);
        if (Object.keys(fieldErrors).length) return setErrors(fieldErrors);
      }
      toast.error(errorMessage(e, t("common.error")));
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  const reload = async () => {
    if (!property) return;
    await qc.invalidateQueries({ queryKey: ["property", property.id] });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {conflict && (
        <Card role="alert" className="border-warning bg-warning/10">
          <p className="mb-2 text-sm font-medium">{t("common.conflict")}</p>
          <Button variant="outline" size="sm" onClick={() => void reload()}>
            {t("common.reload")}
          </Button>
        </Card>
      )}

      <Card className="grid gap-4 md:grid-cols-2">
        <Field label={t("props.type")} error={errors.type}>
          {(id) => (
            <Select id={id} value={f.type} onChange={(e) => set("type", e.target.value as PType)}>
              {(Object.keys(TYPE_KEYS) as PType[]).map((k) => (
                <option key={k} value={k}>
                  {t(TYPE_KEYS[k])}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("props.status")} error={errors.status} hint={t("props.status_hint")}>
          {(id) => (
            <Select
              id={id}
              value={f.status}
              onChange={(e) => set("status", e.target.value as Status)}
            >
              {(Object.keys(STATUS_KEYS) as Status[]).map((k) => (
                <option key={k} value={k}>
                  {t(STATUS_KEYS[k])}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("props.region")} error={errors.region}>
          {(id) => (
            <Input id={id} value={f.region} onChange={(e) => set("region", e.target.value)} />
          )}
        </Field>
        <Field label={t("props.district")} error={errors.district}>
          {(id) => (
            <Input id={id} value={f.district} onChange={(e) => set("district", e.target.value)} />
          )}
        </Field>
        <Field label={t("props.address")} error={errors.address} className="md:col-span-2">
          {(id) => (
            <Input
              id={id}
              required
              value={f.address}
              onChange={(e) => set("address", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("props.house_number")} error={errors.house_number}>
          {(id) => (
            <Input
              id={id}
              value={f.house_number}
              onChange={(e) => set("house_number", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("props.floor")} error={errors.floor}>
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              value={f.floor}
              onChange={(e) => set("floor", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("props.area")} error={errors.area_m2}>
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              step="0.01"
              value={f.area_m2}
              onChange={(e) => set("area_m2", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("props.installed_at")} error={errors.installed_at}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={f.installed_at}
              onChange={(e) => set("installed_at", e.target.value)}
            />
          )}
        </Field>
        <Field label={t("props.warranty_until")} error={errors.warranty_until}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={f.warranty_until}
              onChange={(e) => set("warranty_until", e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t("props.owner_phone")}
          error={errors.owner_phone}
          hint={t("props.owner_phone_hint")}
        >
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              placeholder="90 123 45 67"
              value={f.owner_phone}
              onChange={(e) => set("owner_phone", e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t("props.notes")}
          error={errors.notes}
          className="md:col-span-2"
          hint={t("props.notes_hint")}
        >
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              value={f.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          )}
        </Field>
        <Checkbox
          className="md:col-span-2"
          label={
            <>
              {t("props.show_address")}{" "}
              <span className="text-muted-foreground">— {t("props.show_address_hint")}</span>
            </>
          }
          checked={f.show_address_publicly}
          onChange={(e) => set("show_address_publicly", e.target.checked)}
        />
      </Card>

      {admin && (
        <Card>
          <h3 className="mb-2 font-semibold">{t("props.electricians")}</h3>
          {electricians.data?.results.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("users.empty_electricians")}</p>
          )}
          <div className="grid gap-x-4 sm:grid-cols-2">
            {electricians.data?.results.map((u) => (
              <Checkbox
                key={u.id}
                label={u.full_name || u.phone}
                checked={f.electricians.includes(u.id)}
                onChange={(e) =>
                  set(
                    "electricians",
                    e.target.checked
                      ? [...f.electricians, u.id]
                      : f.electricians.filter((x) => x !== u.id),
                  )
                }
              />
            ))}
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending || !f.address.trim()}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
